/**
 * API service for saved search management
 */

import { getApiClient } from "./apiClient";

export interface SavedSearchData {
  id?: string;
  name: string;
  description?: string;
  search_type: "organizations" | "facilities" | "projects" | "sources";
  filters: Record<string, string>; // URL parameters as key-value pairs
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  created_by_name?: string;
}

/**
 * List all saved searches, optionally filtered by search type
 */
export const listSavedSearches = async (
  searchType?: string,
): Promise<SavedSearchData[]> => {
  try {
    const params = searchType ? { search_type: searchType } : {};
    const client = await getApiClient();
    const response = await client.get("/entities/saved-searches/", {
      params,
    });
    // Handle both paginated and non-paginated responses
    const data = response.data;
    return Array.isArray(data) ? data : data.results || [];
  } catch (error) {
    throw error;
  }
};

/**
 * Get a single saved search by ID
 */
export const getSavedSearch = async (id: string): Promise<SavedSearchData> => {
  try {
    const client = await getApiClient();
    const response = await client.get(`/entities/saved-searches/${id}/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a new saved search
 */
export const createSavedSearch = async (
  data: SavedSearchData,
): Promise<SavedSearchData> => {
  try {
    const client = await getApiClient();
    const response = await client.post("/entities/saved-searches/", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update an existing saved search
 */
export const updateSavedSearch = async (
  id: string,
  data: Partial<SavedSearchData>,
): Promise<SavedSearchData> => {
  try {
    const client = await getApiClient();
    const response = await client.patch(
      `/entities/saved-searches/${id}/`,
      data,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete a saved search
 */
export const deleteSavedSearch = async (id: string): Promise<void> => {
  try {
    const client = await getApiClient();
    await client.delete(`/entities/saved-searches/${id}/`);
  } catch (error) {
    throw error;
  }
};
